import { useEffect, useRef, useState } from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { AlertCircleIcon, Button, CloseIcon, InfoCircleIcon, SparklesIcon } from "@wato/ui";
import { useMayUseConsole } from "../console/access";
import { AssistantInbox } from "./inbox/AssistantInbox";
import { OwnerAssistantChat } from "./OwnerAssistantChat";
import { useOwnerAssistant, type AssistantTab } from "./OwnerAssistantProvider";
import { MOBILE_QUERY } from "../inbox/useIsMobile";

const PHONE_QUERY = MOBILE_QUERY;
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** True below `md` (where the sheet is modal with a backdrop). */
function useIsPhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return phone;
}

/**
 * OWN-163: right-side panel on `md:` (inline-end, so it follows RTL) and bottom sheet on phones.
 * Esc closes; on phones the sheet is modal (aria-modal + Tab trapped inside); focus goes to the textarea on open and returns to the opener on close.
 */
export function OwnerAssistantPanel() {
  const { open, setOpen } = useOwnerAssistant();
  if (!open) return null;
  return <OpenPanel onClose={() => setOpen(false)} />;
}

const TABS: readonly { id: AssistantTab; icon: typeof SparklesIcon }[] = [
  { id: "chat", icon: SparklesIcon },
  { id: "summaries", icon: InfoCircleIcon },
  { id: "questions", icon: InfoCircleIcon },
  { id: "proposals", icon: AlertCircleIcon },
];

function OpenPanel({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { tab, setTab, clearUnread } = useOwnerAssistant();
  const mayUseInbox = useMayUseConsole();
  const ref = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const phone = useIsPhone();
  const phoneRef = useRef(phone);
  phoneRef.current = phone;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.querySelector("textarea")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) closeRef.current();
      if (event.key !== "Tab" || !phoneRef.current) return;
      const panel = ref.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <>
      <div
        aria-hidden="true"
        data-testid="assistant-backdrop"
        className="fixed inset-0 z-40 bg-fg/40 md:hidden"
        onClick={onClose}
      />
      <section
        ref={ref}
        role="dialog"
        aria-modal={phone ? "true" : undefined}
        aria-label={t("dashboard.ownerAssistant.title")}
        data-testid="owner-assistant-panel"
        className="fixed z-50 flex flex-col gap-4 border-line bg-surface p-5 shadow-lg max-md:inset-x-0 max-md:bottom-0 max-md:h-[85vh] max-md:rounded-t-2xl max-md:border-t max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:inset-y-0 md:end-0 md:w-[26rem] md:border-s"
      >
        <header className="flex items-center justify-between gap-2">
          <h2 className="m-0 text-xl font-bold text-fg">{t("dashboard.ownerAssistant.title")}</h2>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("dashboard.ownerAssistant.close")}
            title={t("dashboard.ownerAssistant.close")}
            onClick={onClose}
            startIcon={<CloseIcon />}
          >
            <span className="sr-only">{t("dashboard.ownerAssistant.close")}</span>
          </Button>
        </header>
        {mayUseInbox ? (
          <div
            role="group"
            aria-label={t("dashboard.b29b.assistant.tabs")}
            className="flex flex-wrap gap-1"
          >
            {TABS.map(({ id, icon: Icon }) => (
              <Button
                key={id}
                size="sm"
                variant={tab === id ? "primary" : "secondary"}
                aria-pressed={tab === id}
                data-testid={`assistant-tab-${id}`}
                onClick={() => setTab(id)}
                startIcon={<Icon className="size-4" />}
              >
                {t(`dashboard.b29b.assistant.${id}` as MessageKey)}
              </Button>
            ))}
          </div>
        ) : null}
        {tab === "chat" || !mayUseInbox ? (
          <OwnerAssistantChat />
        ) : (
          <AssistantInbox key={tab} section={tab} onRead={clearUnread} />
        )}
      </section>
    </>
  );
}
